import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Shield,
    CheckCircle,
    XCircle,
    AlertTriangle,
    BarChart3,
    TrendingUp,
    Database,
} from 'lucide-react';
import { fetchDQSummary } from '../data/adapters';
import { DQSummary } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const HealthAnalysisTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [data, setData] = useState<DQSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const dqData = await fetchDQSummary(isDemo).catch(() => null);
                setData(dqData);
                if (!dqData) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const getPassColor = (passPct: number) => {
        if (passPct >= 95) return 'text-green-400';
        if (passPct >= 85) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getPassBarColor = (passPct: number) => {
        if (passPct >= 95) return 'bg-green-500';
        if (passPct >= 85) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* Data Quality Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || !data) {
        return (
            <div className="space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-8 text-center"
                >
                    <div className="flex items-center justify-center mb-4">
                        <Shield size={32} className="text-blue-400 mr-3" />
                        <h2 className="text-2xl font-bold text-white">Data Quality Gate</h2>
                    </div>
                    <div className="text-6xl font-bold text-slate-600 mb-4">--%</div>
                    <p className="text-slate-500">No data available</p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                        'Completeness',
                        'Accuracy',
                        'Consistency',
                        'Timeliness',
                        'Validity',
                        'Uniqueness',
                    ].map((family, index) => (
                        <motion.div
                            key={family}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="evolut-metric-card p-6"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-slate-400">{family}</h3>
                                <AlertTriangle size={20} className="text-slate-500" />
                            </div>
                            <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                            <p className="text-sm text-slate-500">No data available</p>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Top Rule Failures</h3>
                    {renderNoDataPlaceholder('Rule failure data')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Rule Family Summary
                        </h3>
                        {renderNoDataPlaceholder('Rule family data')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                        {renderNoDataPlaceholder('Recommendations')}
                    </motion.div>
                </div>
            </div>
        );
    }

    const overallPassPct = Math.round(
        data.ruleFamilies.reduce((sum, family) => sum + family.passPct, 0) /
            data.ruleFamilies.length
    );

    return (
        <div className="space-y-6">
            {/* Overall Health Score */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="evolut-metric-card p-8 text-center"
            >
                <div className="flex items-center justify-center mb-4">
                    <Shield size={32} className="text-blue-400 mr-3" />
                    <h2 className="text-2xl font-bold text-white">Data Quality Gate</h2>
                </div>
                <div className="text-6xl font-bold text-white mb-4">{overallPassPct}%</div>
                <div className="w-full max-w-md mx-auto bg-slate-700 rounded-full h-3 mb-4">
                    <div
                        className={`h-3 rounded-full transition-all duration-500 ${getPassBarColor(overallPassPct)}`}
                        style={{ width: `${overallPassPct}%` }}
                    />
                </div>
                <p className="text-slate-400">
                    Overall data quality pass rate across all rule families
                </p>
            </motion.div>

            {/* Rule Family Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.ruleFamilies.map((family, index) => (
                    <motion.div
                        key={family.family}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">{family.family}</h3>
                            {family.passPct >= 95 ? (
                                <CheckCircle size={20} className="text-green-400" />
                            ) : family.passPct >= 85 ? (
                                <AlertTriangle size={20} className="text-yellow-400" />
                            ) : (
                                <XCircle size={20} className="text-red-400" />
                            )}
                        </div>
                        <div className={`text-3xl font-bold mb-2 ${getPassColor(family.passPct)}`}>
                            {family.passPct}%
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                            <div
                                className={`h-2 rounded-full transition-all duration-500 ${getPassBarColor(family.passPct)}`}
                                style={{ width: `${family.passPct}%` }}
                            />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Failures</span>
                            <span className="text-white font-semibold">{family.failCount}</span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Top Failures */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="evolut-metric-card p-6"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Top Rule Failures</h3>
                    <div className="flex items-center space-x-2 text-sm text-slate-400">
                        <XCircle size={16} />
                        <span>Rules with highest failure counts</span>
                    </div>
                </div>

                <div className="space-y-4">
                    {data.topFailures.map((failure, index) => (
                        <motion.div
                            key={failure.ruleId}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <span className="font-mono text-sm text-blue-400">
                                            {failure.ruleId}
                                        </span>
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300`}
                                        >
                                            {failure.family}
                                        </span>
                                    </div>
                                    <div className="text-sm text-slate-400 mb-2">
                                        Impact: {failure.impactEntities.join(', ')}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-red-400">
                                        {failure.failCount}
                                    </div>
                                    <div className="text-sm text-slate-400">failures</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Rule Family Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Rule Family Summary</h3>
                    <div className="space-y-4">
                        {data.ruleFamilies.map(family => (
                            <div
                                key={family.family}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                                <span className="text-slate-300">{family.family}</span>
                                <div className="flex items-center space-x-3">
                                    <span
                                        className={`text-sm font-semibold ${getPassColor(family.passPct)}`}
                                    >
                                        {family.passPct}%
                                    </span>
                                    <span className="text-sm text-slate-400">
                                        ({family.failCount} fails)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Recommendations */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                    <div className="space-y-4">
                        {overallPassPct < 90 && (
                            <div className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                                <Shield size={16} className="text-red-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-red-300">
                                        Critical: Overall DQ Below 90%
                                    </h4>
                                    <p className="text-sm text-red-200">
                                        Data quality is below acceptable thresholds. Immediate
                                        attention required.
                                    </p>
                                </div>
                            </div>
                        )}

                        {data.ruleFamilies.some(f => f.passPct < 85) && (
                            <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                                <AlertTriangle size={16} className="text-yellow-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-yellow-300">
                                        Review Low-Performing Rules
                                    </h4>
                                    <p className="text-sm text-yellow-200">
                                        Some rule families are below 85% pass rate. Review and
                                        update validation logic.
                                    </p>
                                </div>
                            </div>
                        )}

                        {data.topFailures.length > 0 && (
                            <div className="flex items-start space-x-3 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                                <BarChart3 size={16} className="text-blue-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-blue-300">
                                        Investigate Top Failures
                                    </h4>
                                    <p className="text-sm text-blue-200">
                                        Focus on rules with highest failure counts to maximize
                                        impact.
                                    </p>
                                </div>
                            </div>
                        )}

                        {overallPassPct >= 95 && (
                            <div className="flex items-start space-x-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                                <TrendingUp size={16} className="text-green-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-green-300">
                                        Excellent Data Quality
                                    </h4>
                                    <p className="text-sm text-green-200">
                                        All rule families are performing above 95%. Continue
                                        monitoring for any degradation.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
